import { Col, Row, Typography } from 'antd';
import { get, isNumber } from 'lodash-es';
import { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import { useRouteMatch } from 'react-router';

import { NewShipmentDateSpecialValue } from '../../app/data/newShipmentConversions';
import { formToDetailLocation } from '../../app/data/shipmentConversions';
import {
  formatCodeValue,
  formatCodeValueShort,
  formatFlight,
  formatLocationLong,
} from '../../common/utils/formatUtils';
import { FormSanitizationContext } from '../../components/forms/FormSanitizationContext';
import { YesNoFormat } from '../../components/forms/checkable';
import { extractLabelFromRichFormValue } from '../../components/forms/dynamic/dynamicFormHelpers';
import {
  useDynamicFormSchemaDynamicFieldsDefinition,
  useDynamicFormSchemaFieldExists,
} from '../../components/forms/dynamic/dynamicFormSchema';
import { isFormSectionNonEmpty } from '../../components/forms/formHelpers';
import { ConfigurableCard } from '../../components/layout/cardElements';
import { TwoLinesSeparator } from '../../components/layout/layoutElements';
import { useLayoutElementsStyles } from '../../components/layout/layoutElements.styles';
import { LabelWithValue } from '../../components/typography';
import {
  formatDateTimeSchedule,
  formatShipperSpecificInfoField,
} from '../../containers/shipmentDetail/shipmentFormats';
import { useNewOrderFeatures } from '../../forms/shipments/shipmentModes';
import { DateTimeFormatMedium } from '../../utils/dateFormatting';
import routes from '../../app/routes';
import { useShipmentOrderPreviewCardStyles } from './ShipmentOrderPreviewCard.styles';

function Section({ titleId, children }) {
  const { styles } = useShipmentOrderPreviewCardStyles();
  return (
    <>
      <div className={styles.shipmentOrderPreviewCardSection}>
        {titleId && (
          <Typography.Title level={4}>
            <FormattedMessage id={titleId} />
          </Typography.Title>
        )}
        {children}
      </div>
      <TwoLinesSeparator className="hide-last" />
    </>
  );
}

function FlightInformation({ formValues }) {
  const { styles } = useShipmentOrderPreviewCardStyles();
  const { styles: layoutStyles } = useLayoutElementsStyles();
  const { flight, airwayBillNumber } = formValues || {};
  const { airline, flightNumber, arrivalDatetime } = flight || {};

  if (!airline && !flightNumber && !arrivalDatetime) {
    return null;
  }

  return (
    <>
      <Row>
        <Col>
          <LabelWithValue
            labelSize="full"
            labelId="book.newAirRetrieval.airwayBillNumber"
            text={airwayBillNumber}
          />
        </Col>
        <Col className={layoutStyles.flex1}>
          <LabelWithValue
            className={styles.shipmentOrderPreviewDateLabelWithValue}
            labelSize="full"
            labelId="shipmentDetail.airlineFlightStatus.arrive"
            text={formatDateTimeSchedule(
              {
                scheduled: moment(arrivalDatetime),
              },
              DateTimeFormatMedium
            )}
          />
        </Col>
      </Row>
      <div className="nl-as-newline">{formatFlight(flight)}</div>
    </>
  );
}

function AddressInformation({ formValues, dateLabelId, dateName }) {
  const { styles } = useShipmentOrderPreviewCardStyles();
  const { styles: layoutStyles } = useLayoutElementsStyles();
  const { companyName, [dateName]: date } = formValues;
  const dateSpecialValue = formValues[`${dateName}-specialValue`];
  const { address, contact } = formValues;
  const location = formToDetailLocation({ address, contact });

  if (
    !isFormSectionNonEmpty(address) &&
    !isFormSectionNonEmpty(contact) &&
    !isFormSectionNonEmpty(companyName) &&
    !date &&
    !dateSpecialValue
  ) {
    return null;
  }

  return (
    <>
      <Row>
        <Col>
          <LabelWithValue
            labelSize="full"
            labelId="shipmentDetail.company"
            text={companyName}
          />
        </Col>
        <Col className={layoutStyles.flex1}>
          <LabelWithValue
            className={styles.shipmentOrderPreviewDateLabelWithValue}
            labelSize="full"
            labelId={dateLabelId}
            text={formatDateTimeSchedule(
              {
                scheduled: date,
                isAsap: dateSpecialValue === NewShipmentDateSpecialValue.ASAP,
                isNow: dateSpecialValue === NewShipmentDateSpecialValue.NOW,
              },
              DateTimeFormatMedium
            )}
          />
        </Col>
      </Row>
      <div className="nl-as-newline">{formatLocationLong(location)}</div>
    </>
  );
}

function ServiceInformation({ formValues }) {
  const shipperSpecificFields = useDynamicFormSchemaDynamicFieldsDefinition({
    schemaName: 'serviceInformation.shipperSpecificInfo',
  });
  const { dangerousGoods } = useNewOrderFeatures();
  const showIncoterms = useDynamicFormSchemaFieldExists({
    schemaName: 'serviceInformation.incoterms',
  });

  if (!isFormSectionNonEmpty(formValues)) {
    return null;
  }

  return (
    <>
      <LabelWithValue
        labelSize="lg"
        nowrap
        labelId="book.newShipment.label.serviceType"
        text={formatCodeValue(formValues.serviceType)}
      />
      <LabelWithValue
        labelSize="lg"
        nowrap
        labelId="book.newShipment.label.commodity"
        text={formValues.commodity}
      />
      {dangerousGoods && (
        <>
          <LabelWithValue
            labelSize="lg"
            nowrap
            labelId="book.newShipment.label.dangerousGoods"
            text={
              <YesNoFormat
                className="text-uppercase"
                value={get(formValues, 'dangerousGoods')}
              />
            }
          />
          <LabelWithValue
            labelSize="lg"
            nowrap
            labelId="book.newShipment.label.exceptedQuantity"
            text={
              <YesNoFormat
                className="text-uppercase"
                value={get(formValues, 'exceptedQuantity')}
              />
            }
          />
          <LabelWithValue
            labelSize="lg"
            nowrap
            labelId="book.newShipment.label.unNumber"
            text={formatCodeValueShort(get(formValues, 'unNumber'))}
          />
          <LabelWithValue
            labelSize="lg"
            nowrap
            labelId="book.newShipment.label.unDescription"
            text={formatCodeValue(get(formValues, 'unNumber'))}
          />
        </>
      )}
      <LabelWithValue
        labelSize="lg"
        nowrap
        labelId="book.newShipment.label.hawbBol"
        text={formValues.bolNumber}
      />
      <LabelWithValue
        labelSize="lg"
        nowrap
        labelId="book.newShipment.label.costCenter"
        text={formatCodeValue(formValues.costCenter)}
      />
      <LabelWithValue
        labelSize="lg"
        nowrap
        labelId="book.newShipment.label.currency"
        text={formatCodeValue(formValues.currency)}
      />
      <LabelWithValue
        labelSize="lg"
        nowrap
        labelId="book.newShipment.label.declaredValue"
        text={formValues.declaredValue}
      />
      <LabelWithValue
        labelSize="lg"
        nowrap
        labelId="book.newShipment.label.additionalInsurance"
        text={formValues.additionalInsurance}
      />
      <LabelWithValue
        labelSize="lg"
        nowrap
        labelId="book.newShipment.label.eoriNumber"
        text={formValues.eoriNumber}
      />
      {showIncoterms && formValues.incoterms && (
        <LabelWithValue
          labelSize="lg"
          nowrap
          labelId="book.newShipment.label.incoterms"
          text={formatCodeValue(formValues.incoterms)}
        />
      )}
      <LabelWithValue
        labelSize="lg"
        nowrap
        labelId="book.newShipment.label.refNo"
        text={formValues.refNo}
      />
      {formValues.hsInfo
        ?.filter(item => isFormSectionNonEmpty(item))
        ?.map((item, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <Fragment key={i}>
            <LabelWithValue
              labelSize="lg"
              nowrap
              labelId="book.newShipment.label.hsCode"
              text={item.code}
            />
            <LabelWithValue
              labelSize="lg"
              nowrap
              labelId="book.newShipment.label.hsDescription"
              text={item.description}
            />
          </Fragment>
        ))}
      {shipperSpecificFields.map(({ name, label, ...info }) => {
        const text = formatShipperSpecificInfoField({
          ...info,
          value: extractLabelFromRichFormValue(
            formValues.shipperSpecificInfo?.[name]
          ),
        });
        return (
          <LabelWithValue
            key={name}
            labelSize="lg"
            nowrap
            label={label}
            text={text}
          />
        );
      })}
    </>
  );
}

export default function ShipmentOrderPreviewCard({
  account,
  values,
  stepIndex,
}) {
  const { styles } = useShipmentOrderPreviewCardStyles();
  // It would be annoying to see every change here immediately,
  // therefore we use cached values from the last step change
  const prevIndex = useRef(stepIndex);
  const [cachedValues, setCachedValues] = useState(values);
  const { asciiWarningShown } = useContext(FormSanitizationContext);
  const isAirRetrieval = !!useRouteMatch(routes.book.newAirRetrieval);

  useEffect(() => {
    if (stepIndex !== prevIndex.current) {
      setCachedValues({ ...values, asciiWarningShown });
      prevIndex.current = stepIndex;
    }
  }, [asciiWarningShown, stepIndex, values]);

  const usedValues = isNumber(stepIndex) ? cachedValues : values;

  return (
    <ConfigurableCard
      titleId="book.newShipment.shipmentPreview"
      className={styles.shipmentOrderPreviewCard}
      bodyComponent="div"
    >
      <Section>
        <LabelWithValue
          labelSize="lg"
          nowrap
          labelId="shipmentDetail.accountNumber"
          text={get(account, 'number')}
        />
      </Section>
      {cachedValues.asciiWarningShown && (
        <div className={styles.shipmentOrderPreviewWarning}>
          <FormattedMessage id="book.newShipment.shipmentPreview.diacriticsWarning" />
        </div>
      )}
      {isAirRetrieval ? (
        <Section titleId="book.newShipment.step.fromFlight">
          <FlightInformation formValues={usedValues?.origin || {}} />
        </Section>
      ) : (
        <Section titleId="book.newShipment.step.fromAddress">
          <AddressInformation
            formValues={usedValues?.origin || {}}
            dateLabelId="job.readyDate.short"
            dateName="pickupDateTime"
          />
        </Section>
      )}
      <Section titleId="book.newShipment.step.toAddress">
        <AddressInformation
          formValues={usedValues?.destination || {}}
          dateLabelId="job.deadlineDate.short"
          dateName="deliveryDateTime"
        />
      </Section>
      <Section titleId="book.newShipment.step.shipmentInformation">
        <ServiceInformation formValues={usedValues?.serviceInformation || {}} />
      </Section>
    </ConfigurableCard>
  );
}
